import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { CategorySelector } from "@/components/CategorySelector";
import { PromptCard } from "@/components/PromptCard";
import { ChatInterface } from "@/components/ChatInterface";
import {
  SubmissionEditor,
  SubmissionEditorRef,
} from "@/components/SubmissionEditor";
import { ReviewSection } from "@/components/ReviewSection";
import { Button } from "@/components/ui/button";
import { Book, Pen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getOneQuestionByCategory } from "@/api/questionApi";
import { ChallengeCategory, CategoryLabels } from "@/components/types/types";
import { generateImage } from "@/api/llmApi";

export default function PromptPage() {
  const [selectedCategory, setSelectedCategory] = useState<ChallengeCategory>(
    ChallengeCategory.Poem
  );
  const [submissionContent, setSubmissionContent] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [submittedContent, setSubmittedContent] = useState("");
  const [questionId, setQuestionId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const submissionEditorRef = useRef<SubmissionEditorRef>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null
  );

  const categoryName = CategoryLabels[selectedCategory];

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        setIsLoading(true);
        const questionData = await getOneQuestionByCategory(selectedCategory);
        setPrompt(questionData.content.content);
        setQuestionId(questionData.id);
      } catch (error) {
        console.error("Error fetching prompt:", error);
        toast({
          title: "Error",
          description: "Failed to load prompt. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompt();
  }, [selectedCategory, toast]);

  const handleSubmit = async (content: string) => {
    if (content.trim().length < 50) {
      toast({
        title: "Content too short",
        description: "Please write more before submitting.",
        variant: "destructive",
      });
      return;
    }

    // Save the submitted content and show review immediately
    setSubmittedContent(content);
    setShowReview(true);

    toast({
      title: "Submission Received!",
      description: "Now you can rate other submissions.",
    });

    // Generate image asynchronously
    generateImage(content, questionId)
      .then((imageResponse) => {
        setGeneratedImageUrl(imageResponse.imageUrl || null);
      })
      .catch((error) => {
        console.error("Error generating image:", error);
        toast({
          title: "Note",
          description:
            "Image generation failed, but your submission is still available.",
        });
      });
  };

  const handleAddToSubmission = (content: string) => {
    // Use the ref to directly add content to the editor
    if (submissionEditorRef.current) {
      submissionEditorRef.current.addContent(content);

      toast({
        title: "Added to submission",
        description: "AI response added to your submission editor.",
      });
    }
  };

  const handleReviewComplete = () => {
    toast({
      title: "Rankings Submitted!",
      description: "Your submission is now live on the leaderboard.",
    });

    setTimeout(() => {
      navigate("/leaderboard");
    }, 1500);
  };

  return (
    <div className="min-h-screen pb-16">
      <Navbar />

      <main className="container max-w-5xl pt-20 px-4">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
          <CategorySelector
            selectedCategory={selectedCategory}
            onChange={setSelectedCategory}
            className="w-full sm:w-[180px]"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-4">
            <PromptCard
              prompt={prompt}
              category={selectedCategory}
              isLoading={isLoading}
              questionId={questionId}
            />
          </div>

          <div className="lg:col-span-2">
            <div className="w-full">
              <div className="glass-card bg-card/30 p-4 rounded-lg">
                {!showReview ? (
                  <>
                    <h3 className="text-lg font-bold mb-2 font-heading">
                      AI Chat Assistant
                    </h3>
                    <ChatInterface
                      onAddToSubmission={handleAddToSubmission}
                    />

                    <SubmissionEditor
                      ref={submissionEditorRef}
                      onSubmit={handleSubmit}
                      content={submissionContent}
                      setContent={setSubmissionContent}
                      questionId={questionId}
                    />
                  </>
                ) : (
                  <ReviewSection
                    userSubmission={submittedContent}
                    category={selectedCategory}
                    onComplete={handleReviewComplete}
                    generatedImageUrl={generatedImageUrl}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
